import { useState, useEffect, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Banknote, Plus, Calculator, Check, Download, FileText, Search, Users,
  TrendingUp, Clock, DollarSign, Shield, Building2, AlertTriangle,
  CheckCircle2, ArrowRight, Edit, Eye, RefreshCw, Zap, Settings,
  Brain, Sparkles, BarChart3, UserX, Scale, Lightbulb, Activity
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const ax = () => { const t = localStorage.getItem("token"); return { headers: t ? { Authorization: `Bearer ${t}` } : {} }; };
const fmt = (n) => `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;
const STATUS_MAP = {
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-700" },
  dispersed: { label: "Dispersada", cls: "bg-blue-100 text-blue-700" }
};

const Payroll = () => {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("nominas");
  const [searchTerm, setSearchTerm] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [workCenterFilter, setWorkCenterFilter] = useState("all");

  // Dialogs
  const [showCreateDlg, setShowCreateDlg] = useState(false);
  const [showBatchDlg, setShowBatchDlg] = useState(false);
  const [showDetailDlg, setShowDetailDlg] = useState(false);
  const [showEditDlg, setShowEditDlg] = useState(false);
  const [showProviderDlg, setShowProviderDlg] = useState(false);
  const [showDisperserDlg, setShowDisperserDlg] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Sprint 2
  const [showFiniquitoDlg, setShowFiniquitoDlg] = useState(false);
  const [finiquitoResult, setFiniquitoResult] = useState(null);
  const [finiquitoAI, setFiniquitoAI] = useState(null);
  const [finiquitoForm, setFiniquitoForm] = useState({ employee_id: "", reason: "renuncia_voluntaria" });
  const [finiquitoLoading, setFiniquitoLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [providerConfig, setProviderConfig] = useState({ environment: "sandbox", origin_clabe: "", counterparty_bank_code: "90646", stp_company_code: "HUMANOZ", credentials: { username: "", password: "", private_key_passphrase: "" } });
  const [disperserPreview, setDisperserPreview] = useState(null);
  const [disperserBatches, setDisperserBatches] = useState([]);
  const [disperserLoading, setDisperserLoading] = useState(false);

  // Forms
  const [newPayroll, setNewPayroll] = useState({
    employee_id: "", base_salary: "", period: new Date().toISOString().slice(0, 7),
    period_type: "quincenal", period_days: "15", days_worked: "15",
    overtime_hours: "0", sunday_worked: "0", bonuses: "0",
    vacation_premium: "0", other_perceptions: "0", infonavit: "0",
    pension_alimenticia: "0", other_deductions: "0", absences: "0", disabilities: "0"
  });
  const [batchPeriod, setBatchPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [batchType, setBatchType] = useState("quincenal");
  const [batchLoading, setBatchLoading] = useState(false);

  const workCenters = useMemo(() => {
    const seen = new Map();
    employees.forEach(employee => {
      if (employee.work_center_id && !seen.has(employee.work_center_id)) {
        seen.set(employee.work_center_id, { id: employee.work_center_id, name: employee.work_center_name });
      }
    });
    return Array.from(seen.values());
  }, [employees]);

  const fetchData = useCallback(async () => {
    try {
      const query = workCenterFilter !== "all" ? `?work_center_id=${workCenterFilter}` : "";
      const [recRes, empRes, stRes, anRes, providerRes, batchRes] = await Promise.all([
        axios.get(`${API}/payroll${query}`, ax()),
        axios.get(`${API}/employees`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/payroll/stats${query}`, ax()).catch(() => ({ data: null })),
        axios.get(`${API}/payroll/analytics`, ax()).catch(() => ({ data: null })),
        axios.get(`${API}/payroll/provider-config`, ax()).catch(() => ({ data: null })),
        axios.get(`${API}/payroll/disperser/batches`, ax()).catch(() => ({ data: [] })),
      ]);
      setRecords(recRes.data || []);
      setEmployees(empRes.data || []);
      setStats(stRes.data);
      setAnalytics(anRes.data);
      if (providerRes.data) setProviderConfig(previous => ({ ...previous, ...providerRes.data }));
      setDisperserBatches(batchRes.data || []);
    } catch { toast.error("Error al cargar datos"); } finally { setLoading(false); }
  }, [workCenterFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-fill salary when employee selected
  const onEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.employee_id === empId);
    setNewPayroll(prev => ({
      ...prev, employee_id: empId,
      base_salary: emp?.salary ? String(emp.salary) : prev.base_salary
    }));
  };

  const handleCreate = async () => {
    if (!newPayroll.employee_id || !newPayroll.base_salary) { toast.error("Empleado y salario requeridos"); return; }
    try {
      const payload = {};
      for (const [k, v] of Object.entries(newPayroll)) {
        payload[k] = k === "employee_id" || k === "period" || k === "period_type" ? v : parseFloat(v) || 0;
      }
      await axios.post(`${API}/payroll`, payload, ax());
      toast.success("Nómina creada con cálculos mexicanos");
      setShowCreateDlg(false);
      resetForm();
      fetchData();
    } catch { toast.error("Error al crear nómina"); }
  };

  const handleBatch = async () => {
    setBatchLoading(true);
    try {
      const res = await axios.post(`${API}/payroll/process-batch`, { period: batchPeriod, period_type: batchType }, ax());
      toast.success(`Nómina procesada: ${res.data.processed} empleados (${res.data.skipped} ya existían)`);
      setShowBatchDlg(false);
      fetchData();
    } catch { toast.error("Error al procesar"); } finally { setBatchLoading(false); }
  };

  const handleApprove = async (id) => {
    try { await axios.put(`${API}/payroll/${id}/approve`, {}, ax()); toast.success("Aprobada"); fetchData(); }
    catch { toast.error("Error"); }
  };

  const handleApproveBatch = async () => {
    const period = periodFilter || stats?.current_period;
    if (!period) return;
    try {
      const res = await axios.put(`${API}/payroll/approve-batch`, { period }, ax());
      toast.success(`${res.data.approved} nóminas aprobadas`);
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleDownloadReceipt = async (id) => {
    try {
      const res = await axios.get(`${API}/payroll/${id}/receipt`, { ...ax(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `recibo_${id}.txt`; a.click();
    } catch { toast.error("Error"); }
  };

  const handleExportCSV = async () => {
    try {
      const p = periodFilter ? `?period=${periodFilter}` : "";
      const res = await axios.get(`${API}/payroll/export/csv${p}`, { ...ax(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "nomina.csv"; a.click();
    } catch { toast.error("Error"); }
  };

  const openDetail = (r) => { setSelectedRecord(r); setShowDetailDlg(true); };

  // Sprint 2 handlers
  const handleSimulateFiniquito = async () => {
    if (!finiquitoForm.employee_id) { toast.error("Selecciona un empleado"); return; }
    setFiniquitoLoading(true); setFiniquitoResult(null); setFiniquitoAI(null);
    try {
      const res = await axios.post(`${API}/payroll/finiquito-simulator`, finiquitoForm, ax());
      setFiniquitoResult(res.data);
      toast.success("Simulación calculada");
    } catch { toast.error("Error al simular"); } finally { setFiniquitoLoading(false); }
  };

  const handleFiniquitoAI = async () => {
    if (!finiquitoResult) return;
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/payroll/finiquito-ai`, finiquitoResult, ax());
      setFiniquitoAI(res.data);
    } catch { toast.error("Error IA"); } finally { setAiLoading(false); }
  };

  const handlePayrollAI = async () => {
    setAiLoading(true);
    try {
      const res = await axios.get(`${API}/payroll/ai-insights`, ax());
      setAiInsights(res.data);
      toast.success("Insights generados");
    } catch { toast.error("Error IA"); } finally { setAiLoading(false); }
  };

  const resetForm = () => setNewPayroll({
    employee_id: "", base_salary: "", period: new Date().toISOString().slice(0, 7),
    period_type: "quincenal", period_days: "15", days_worked: "15",
    overtime_hours: "0", sunday_worked: "0", bonuses: "0",
    vacation_premium: "0", other_perceptions: "0", infonavit: "0",
    pension_alimenticia: "0", other_deductions: "0", absences: "0", disabilities: "0"
  });

  const periods = [...new Set(records.map(r => r.period).filter(Boolean))].sort().reverse();
  const filtered = records.filter(r => {
    const matchSearch = !searchTerm || (r.employee_name || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchPeriod = !periodFilter || r.period === periodFilter;
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    const matchCenter = workCenterFilter === "all" || r.work_center_id === workCenterFilter;
    return matchSearch && matchPeriod && matchStatus && matchCenter;
  });

  const handleSaveProviderConfig = async () => {
    try {
      await axios.post(`${API}/payroll/provider-config`, providerConfig, ax());
      toast.success("Configuración STP guardada");
      setShowProviderDlg(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar configuración");
    }
  };

  const loadDisperserPreview = async () => {
    setDisperserLoading(true);
    try {
      const payload = { period: periodFilter || stats?.current_period, work_center_id: workCenterFilter !== "all" ? workCenterFilter : undefined };
      const response = await axios.post(`${API}/payroll/disperser/preview`, payload, ax());
      setDisperserPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo preparar la vista previa");
    } finally {
      setDisperserLoading(false);
    }
  };

  const handleCreateDisperserBatch = async () => {
    try {
      const payload = { period: periodFilter || stats?.current_period, work_center_id: workCenterFilter !== "all" ? workCenterFilter : undefined };
      await axios.post(`${API}/payroll/disperser/batches`, payload, ax());
      toast.success("Lote dispersor creado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo crear el lote");
    }
  };

  const handleApproveDisperser = async (batchId, level) => {
    try {
      await axios.put(`${API}/payroll/disperser/batches/${batchId}/approve-level-${level}`, {}, ax());
      toast.success(`Aprobación nivel ${level} registrada`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo aprobar el lote");
    }
  };

  const handleExecuteDisperser = async (batchId) => {
    try {
      const response = await axios.post(`${API}/payroll/disperser/batches/${batchId}/execute`, {}, ax());
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo ejecutar el lote");
    }
  };

  if (loading) return <Layout title="Nómina"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Gestión de Nómina" subtitle="Motor multisede con fiscalidad por centro, partidas mixtas y dispersión enterprise"
      actions={<div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={() => setShowProviderDlg(true)} data-testid="payroll-provider-config-btn"><Settings className="w-4 h-4 mr-2" />STP</Button>
        <Button variant="outline" onClick={() => { setShowDisperserDlg(true); loadDisperserPreview(); }} data-testid="payroll-disperser-btn"><Zap className="w-4 h-4 mr-2" />Dispersor</Button>
        <Button variant="outline" onClick={handleExportCSV} data-testid="export-payroll-btn"><Download className="w-4 h-4 mr-2" />CSV</Button>
        <Button variant="outline" onClick={() => setShowFiniquitoDlg(true)} data-testid="finiquito-btn"><Scale className="w-4 h-4 mr-2" />Finiquito</Button>
        <Button variant="outline" onClick={() => setShowBatchDlg(true)} data-testid="batch-payroll-btn"><Zap className="w-4 h-4 mr-2" />Procesar Masivo</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowCreateDlg(true)} data-testid="create-payroll-btn"><Plus className="w-4 h-4 mr-2" />Nueva Nómina</Button>
      </div>}>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="payroll-kpis">
          {[
            { label: "Total Percepciones", value: fmt(stats.total_nomina), icon: Banknote, color: "bg-emerald-100 text-emerald-600" },
            { label: "Neto a Pagar", value: fmt(stats.total_neto), icon: DollarSign, color: "bg-blue-100 text-blue-600" },
            { label: "ISR Retenido", value: fmt(stats.total_isr), icon: Shield, color: "bg-amber-100 text-amber-600" },
            { label: "IMSS Obrero", value: fmt(stats.total_imss), icon: Users, color: "bg-violet-100 text-violet-600" },
            { label: "Costo Empresa", value: fmt(stats.total_costo_empresa), icon: Building2, color: "bg-red-100 text-red-600" },
            { label: "Horas Extra", value: fmt(stats.total_overtime), icon: Clock, color: "bg-cyan-100 text-cyan-600" },
            { label: "Bonos", value: fmt(stats.total_bonuses), icon: TrendingUp, color: "bg-pink-100 text-pink-600" },
            { label: "Promedio Neto", value: fmt(stats.avg_neto), icon: Calculator, color: "bg-teal-100 text-teal-600" },
            { label: "Procesadas", value: `${stats.processed}/${stats.employees_count}`, icon: CheckCircle2, color: "bg-slate-100 text-slate-600" },
            { label: "Pendientes", value: stats.pending, icon: AlertTriangle, color: "bg-orange-100 text-orange-600" },
          ].map((s, i) => (
            <Card key={i} className="border-slate-200"><CardContent className="p-3.5"><div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.color}`}><s.icon className="w-4 h-4" /></div>
              <div className="min-w-0"><p className="text-base font-bold text-slate-900 truncate">{s.value}</p><p className="text-[10px] text-slate-500">{s.label}</p></div>
            </div></CardContent></Card>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1" data-testid="payroll-tabs">
          <TabsTrigger value="nominas"><Banknote className="w-3.5 h-3.5 mr-1" />Nóminas</TabsTrigger>
          <TabsTrigger value="recibos"><FileText className="w-3.5 h-3.5 mr-1" />Recibos</TabsTrigger>
          <TabsTrigger value="analytics"><BarChart3 className="w-3.5 h-3.5 mr-1" />Analytics</TabsTrigger>
          <TabsTrigger value="enterprise"><Sparkles className="w-3.5 h-3.5 mr-1" />Enterprise</TabsTrigger>
          <TabsTrigger value="ai" className="text-violet-600"><Brain className="w-3.5 h-3.5 mr-1" />IA Insights</TabsTrigger>
        </TabsList>

        {/* NÓMINAS TABLE */}
        <TabsContent value="nominas">
          <Card className="mb-4 border-slate-200">
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Buscar empleado..." className="pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-payroll" />
              </div>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger className="w-[160px]" data-testid="period-filter"><SelectValue placeholder="Período" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_periods">Todos</SelectItem>
                  {periods.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="approved">Aprobada</SelectItem>
                  <SelectItem value="dispersed">Dispersada</SelectItem>
                </SelectContent>
              </Select>
              <Select value={workCenterFilter} onValueChange={setWorkCenterFilter}>
                <SelectTrigger className="w-[180px]" data-testid="payroll-work-center-filter"><SelectValue placeholder="Centro" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los centros</SelectItem>
                  {workCenters.map(center => <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {filtered.some(r => r.status === "pending") && (
                <Button variant="outline" size="sm" onClick={handleApproveBatch} data-testid="approve-all-btn">
                  <Check className="w-3.5 h-3.5 mr-1" />Aprobar Todas
                </Button>
              )}
              <Badge variant="outline">{filtered.length} registros</Badge>
            </CardContent>
          </Card>

          {filtered.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-slate-500" data-testid="payroll-empty">
              <Banknote className="w-14 h-14 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-lg">Sin registros de nómina</p>
              <p className="text-sm mt-1">Procesa la nómina masiva o crea registros individuales</p>
            </CardContent></Card>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Empleado</TableHead><TableHead>Depto</TableHead><TableHead>Centro</TableHead><TableHead>Período</TableHead>
                    <TableHead className="text-right">Percepciones</TableHead>
                    <TableHead className="text-right">ISR</TableHead>
                    <TableHead className="text-right">IMSS</TableHead>
                    <TableHead className="text-right">Deducciones</TableHead>
                    <TableHead className="text-right font-bold">Neto</TableHead><TableHead className="text-right">Externo</TableHead>
                    <TableHead>Estado</TableHead><TableHead className="w-[100px]"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filtered.map(r => {
                      const perc = r.percepciones || {};
                      const ded = r.deducciones || {};
                      const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
                      return (
                        <TableRow key={r.payroll_id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(r)} data-testid={`pay-row-${r.payroll_id}`}>
                          <TableCell><p className="font-medium text-slate-900 text-sm">{r.employee_name || r.employee_id}</p></TableCell>
                          <TableCell className="text-xs text-slate-500">{r.department}</TableCell>
                          <TableCell className="text-xs text-sky-600">{r.work_center_name || "-"}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{r.period}</Badge></TableCell>
                          <TableCell className="text-right text-sm text-emerald-600">{fmt(perc.total || r.base_salary)}</TableCell>
                          <TableCell className="text-right text-sm text-amber-600">{fmt(ded.isr || r.isr)}</TableCell>
                          <TableCell className="text-right text-sm text-violet-600">{fmt(ded.imss_obrero || r.imss)}</TableCell>
                          <TableCell className="text-right text-sm text-red-600">{fmt(ded.total || r.taxes)}</TableCell>
                          <TableCell className="text-right font-bold text-sm">{fmt(r.neto_a_pagar || r.net_salary)}</TableCell>
                          <TableCell className="text-right text-sm text-slate-500">{fmt(r.allocation_summary?.external_total)}</TableCell>
                          <TableCell><Badge className={st.cls}>{st.label}</Badge></TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <div className="flex gap-1">
                              {r.status === "pending" && (
                                <Button size="sm" variant="ghost" onClick={() => handleApprove(r.payroll_id)} data-testid={`approve-${r.payroll_id}`}>
                                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => handleDownloadReceipt(r.payroll_id)} data-testid={`receipt-${r.payroll_id}`}>
                                <Download className="w-3.5 h-3.5 text-blue-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {/* Summary footer */}
          {filtered.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-3 text-center">
                <p className="text-xs text-emerald-600 font-medium">Total Percepciones</p>
                <p className="text-lg font-bold text-emerald-700">{fmt(filtered.reduce((s, r) => s + (r.percepciones?.total || r.base_salary || 0), 0))}</p>
              </CardContent></Card>
              <Card className="border-red-200 bg-red-50"><CardContent className="p-3 text-center">
                <p className="text-xs text-red-600 font-medium">Total Deducciones</p>
                <p className="text-lg font-bold text-red-700">{fmt(filtered.reduce((s, r) => s + (r.deducciones?.total || r.taxes || 0), 0))}</p>
              </CardContent></Card>
              <Card className="border-blue-200 bg-blue-50"><CardContent className="p-3 text-center">
                <p className="text-xs text-blue-600 font-medium">Total Neto</p>
                <p className="text-lg font-bold text-blue-700">{fmt(filtered.reduce((s, r) => s + (r.neto_a_pagar || r.net_salary || 0), 0))}</p>
              </CardContent></Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="enterprise">
          <div className="grid xl:grid-cols-[1.1fr_0.9fr] gap-6">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" />Motor enterprise</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-400 uppercase">Proveedor</p>
                    <p className="text-lg font-bold text-slate-900 uppercase">{providerConfig.provider || "stp"}</p>
                    <p className="text-xs text-slate-500">Entorno: {providerConfig.environment || "sandbox"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-400 uppercase">Credenciales</p>
                    <p className="text-lg font-bold text-slate-900">{providerConfig.credentials_ready ? "Listas" : "Pendientes"}</p>
                    <p className="text-xs text-slate-500">La ejecución real STP se habilita al capturarlas</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {(stats?.by_work_center || []).map(center => (
                    <div key={center.work_center_name} className="p-3 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{center.work_center_name}</p>
                        <p className="text-xs text-slate-500">{center.count} nómina(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900">{fmt(center.neto)}</p>
                        <p className="text-xs text-slate-500">Costo {fmt(center.costo)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4 text-cyan-600" />Lotes de dispersión</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {disperserBatches.length === 0 ? <p className="text-sm text-slate-500">Aún no hay lotes generados.</p> : disperserBatches.slice(0, 8).map(batch => (
                  <div key={batch.batch_id} className="p-4 rounded-xl border border-slate-200 bg-white space-y-3" data-testid={`disperser-batch-${batch.batch_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{batch.batch_id}</p>
                        <p className="text-xs text-slate-500">{batch.period || "Sin período"}</p>
                      </div>
                      <Badge className="bg-slate-100 text-slate-700">{batch.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-500">Interno {fmt(batch.internal_total)} · Externo {fmt(batch.external_total)}</p>
                    <div className="flex gap-2 flex-wrap">
                      {batch.approval_level < 1 && <Button size="sm" variant="outline" onClick={() => handleApproveDisperser(batch.batch_id, 1)} data-testid={`approve-disperser-l1-${batch.batch_id}`}>Aprobar L1</Button>}
                      {batch.approval_level < 2 && <Button size="sm" variant="outline" onClick={() => handleApproveDisperser(batch.batch_id, 2)} data-testid={`approve-disperser-l2-${batch.batch_id}`}>Aprobar L2</Button>}
                      <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => handleExecuteDisperser(batch.batch_id)} data-testid={`execute-disperser-${batch.batch_id}`}>Ejecutar</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* RECIBOS TAB */}
        <TabsContent value="recibos">
          {records.filter(r => r.status === "approved" || r.status === "dispersed").length === 0 ? (
            <Card><CardContent className="p-10 text-center text-slate-500" data-testid="recibos-empty">
              <FileText className="w-14 h-14 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Sin recibos generados</p>
              <p className="text-sm mt-1">Aprueba nóminas para generar recibos descargables</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {records.filter(r => r.status === "approved" || r.status === "dispersed").map(r => (
                <Card key={r.payroll_id} className="border-slate-200 hover:shadow-md transition-all" data-testid={`recibo-card-${r.payroll_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                        {r.employee_name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "??"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">{r.employee_name}</p>
                        <p className="text-xs text-slate-500">{r.department} | {r.period}</p>
                      </div>
                      <Badge className={STATUS_MAP[r.status]?.cls}>{STATUS_MAP[r.status]?.label}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="p-2 rounded-lg bg-emerald-50 text-center"><p className="text-[10px] text-emerald-600">Percepciones</p><p className="text-sm font-bold text-emerald-700">{fmt(r.percepciones?.total)}</p></div>
                      <div className="p-2 rounded-lg bg-red-50 text-center"><p className="text-[10px] text-red-600">Deducciones</p><p className="text-sm font-bold text-red-700">{fmt(r.deducciones?.total)}</p></div>
                    </div>
                    <div className="p-2 rounded-lg bg-blue-50 text-center mb-3"><p className="text-[10px] text-blue-600">Neto a Pagar</p><p className="text-lg font-bold text-blue-700">{fmt(r.neto_a_pagar)}</p></div>
                    <Button variant="outline" size="sm" className="w-full" onClick={() => handleDownloadReceipt(r.payroll_id)} data-testid={`dl-recibo-${r.payroll_id}`}>
                      <Download className="w-3.5 h-3.5 mr-2" />Descargar Recibo
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics">
          {analytics ? (
            <div className="space-y-4">
              {/* Period evolution */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-600" />Evolución por Período</CardTitle></CardHeader>
                <CardContent>
                  {analytics.by_period.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Sin datos</p> : (
                    <div className="space-y-3">
                      {analytics.by_period.map((p, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <Badge variant="outline" className="w-20 text-center text-xs shrink-0">{p.period}</Badge>
                          <div className="flex-1 grid grid-cols-4 gap-2 text-xs">
                            <div className="p-1.5 rounded bg-emerald-50 text-center"><p className="text-emerald-500 text-[9px]">Percepciones</p><p className="font-bold text-emerald-700">{fmt(p.percepciones)}</p></div>
                            <div className="p-1.5 rounded bg-red-50 text-center"><p className="text-red-500 text-[9px]">Deducciones</p><p className="font-bold text-red-700">{fmt(p.deducciones)}</p></div>
                            <div className="p-1.5 rounded bg-blue-50 text-center"><p className="text-blue-500 text-[9px]">Neto</p><p className="font-bold text-blue-700">{fmt(p.neto)}</p></div>
                            <div className="p-1.5 rounded bg-slate-100 text-center"><p className="text-slate-500 text-[9px]">Emps</p><p className="font-bold text-slate-700">{p.count}</p></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              {/* Department distribution */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-violet-600" />Distribución por Departamento</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(analytics.by_department || []).map((d, i) => {
                      const maxCost = Math.max(...(analytics.by_department || []).map(x => x.total_costo));
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">{d.department}</span>
                            <span className="text-xs text-slate-500">{d.count} empleados | Costo: {fmt(d.total_costo)}</span>
                          </div>
                          <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all flex items-center justify-end pr-1.5"
                              style={{ width: `${maxCost > 0 ? Math.max((d.total_costo / maxCost) * 100, 8) : 8}%` }}>
                              <span className="text-[9px] text-white font-bold">{fmt(d.total_neto)}</span>
                            </div>
                          </div>
                          <div className="flex gap-4 mt-1 text-[10px] text-slate-400">
                            <span>ISR: {fmt(d.total_isr)}</span><span>IMSS: {fmt(d.total_imss)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              {/* Top earners */}
              {analytics.top_earners?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" />Top Compensaciones (Último Período)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.top_earners.map((t, i) => (
                        <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-[10px] font-bold">{i + 1}</div>
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{t.name}</p><p className="text-[10px] text-slate-500">{t.department}</p></div>
                          <div className="text-right"><p className="text-sm font-bold text-blue-700">{fmt(t.neto)}</p><p className="text-[10px] text-slate-400">Costo: {fmt(t.costo)}</p></div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Totals */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: "Total Neto Histórico", value: fmt(analytics.totals?.total_neto), color: "bg-blue-50 text-blue-700 border-blue-200" },
                  { label: "Total ISR Retenido", value: fmt(analytics.totals?.total_isr), color: "bg-amber-50 text-amber-700 border-amber-200" },
                  { label: "Total IMSS Obrero", value: fmt(analytics.totals?.total_imss), color: "bg-violet-50 text-violet-700 border-violet-200" },
                  { label: "Total Costo Empresa", value: fmt(analytics.totals?.total_costo), color: "bg-slate-100 text-slate-700 border-slate-300" },
                ].map((t, i) => (
                  <Card key={i} className={`border ${t.color}`}><CardContent className="p-3 text-center"><p className="text-[10px] font-medium">{t.label}</p><p className="text-lg font-bold">{t.value}</p></CardContent></Card>
                ))}
              </div>
            </div>
          ) : <div className="text-center py-12 text-slate-500">Cargando analytics...</div>}
        </TabsContent>

        {/* AI INSIGHTS TAB */}
        <TabsContent value="ai">
          <Card className="border-slate-200 mb-4">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-500" />IA Insights de Nómina</h3>
                <p className="text-xs text-slate-500 mt-1">Detección de anomalías, predicción de costos, optimización fiscal y alertas de cumplimiento</p>
              </div>
              <Button onClick={handlePayrollAI} disabled={aiLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="payroll-ai-btn">
                {aiLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2" />}
                {aiLoading ? "Analizando..." : "Generar Insights"}
              </Button>
            </CardContent>
          </Card>
          {aiInsights ? (
            <div className="space-y-4">
              {aiInsights.anomalies?.length > 0 && (
                <Card className="border-l-4 border-l-red-500"><CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Anomalías Detectadas</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">{aiInsights.anomalies.map((a, i) => (
                    <div key={i} className={`p-3 rounded-lg flex items-start gap-2 ${a.severity === "high" ? "bg-red-50" : a.severity === "medium" ? "bg-amber-50" : "bg-blue-50"}`}>
                      <Badge className={a.severity === "high" ? "bg-red-100 text-red-700" : a.severity === "medium" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}>{a.severity}</Badge>
                      <div><p className="text-xs font-medium text-slate-700">{a.type}</p><p className="text-xs text-slate-600">{a.description}</p></div>
                    </div>
                  ))}</div></CardContent>
                </Card>
              )}
              {aiInsights.cost_forecast && (
                <Card className="border-slate-200"><CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" />Proyección de Costos</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-blue-50 text-center"><p className="text-xs text-blue-500">Próximos 3 meses</p><p className="text-lg font-bold text-blue-700">{fmt(aiInsights.cost_forecast.next_3m)}</p></div>
                      <div className="p-3 rounded-xl bg-violet-50 text-center"><p className="text-xs text-violet-500">Próximos 6 meses</p><p className="text-lg font-bold text-violet-700">{fmt(aiInsights.cost_forecast.next_6m)}</p></div>
                      <div className="p-3 rounded-xl bg-slate-100 text-center"><p className="text-xs text-slate-500">Tendencia</p><p className="text-lg font-bold text-slate-700">{aiInsights.cost_forecast.trend === "up" ? "Subiendo" : aiInsights.cost_forecast.trend === "down" ? "Bajando" : "Estable"}</p></div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {aiInsights.fiscal_optimization?.length > 0 && (
                <Card className="border-l-4 border-l-emerald-500"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-4 h-4 text-emerald-600" />Optimización Fiscal</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">{aiInsights.fiscal_optimization.map((t, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50"><ArrowRight className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{t}</p></div>
                  ))}</div></CardContent>
                </Card>
              )}
              {aiInsights.compliance_alerts?.length > 0 && (
                <Card className="border-l-4 border-l-amber-500"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4 text-amber-600" />Alertas de Cumplimiento</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">{aiInsights.compliance_alerts.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-amber-50"><AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{a}</p></div>
                  ))}</div></CardContent>
                </Card>
              )}
              {aiInsights.department_analysis?.length > 0 && (
                <Card className="border-slate-200"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-violet-600" />Análisis por Departamento</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">{aiInsights.department_analysis.map((d, i) => (
                    <div key={i} className="p-3 rounded-lg border border-slate-100"><p className="text-xs font-semibold text-slate-700">{d.dept}</p><p className="text-xs text-slate-600 mt-0.5">{d.insight}</p></div>
                  ))}</div></CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card><CardContent className="p-12 text-center text-slate-500">
              <Brain className="w-16 h-16 mx-auto mb-4 text-violet-200" />
              <p className="font-medium text-lg text-slate-700">Inteligencia Artificial para Nómina</p>
              <p className="text-sm mt-2 max-w-md mx-auto">Detecta anomalías en pagos, proyecta costos futuros, optimiza la carga fiscal y recibe alertas de cumplimiento regulatorio</p>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* === CREATE PAYROLL DIALOG === */}
      <Dialog open={showCreateDlg} onOpenChange={setShowCreateDlg}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva Nómina Individual</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Empleado *</Label>
                <Select value={newPayroll.employee_id} onValueChange={onEmployeeSelect}>
                  <SelectTrigger data-testid="pay-employee"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name} - {e.department}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Período *</Label><Input type="month" value={newPayroll.period} onChange={e => setNewPayroll(p => ({ ...p, period: e.target.value }))} data-testid="pay-period" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Tipo</Label>
                <Select value={newPayroll.period_type} onValueChange={v => setNewPayroll(p => ({ ...p, period_type: v, period_days: v === "quincenal" ? "15" : "30", days_worked: v === "quincenal" ? "15" : "30" }))}>
                  <SelectTrigger data-testid="pay-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="quincenal">Quincenal</SelectItem><SelectItem value="mensual">Mensual</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Salario Mensual *</Label><Input type="number" value={newPayroll.base_salary} onChange={e => setNewPayroll(p => ({ ...p, base_salary: e.target.value }))} placeholder="25000" data-testid="pay-salary" /></div>
              <div><Label>Días Trabajados</Label><Input type="number" value={newPayroll.days_worked} onChange={e => setNewPayroll(p => ({ ...p, days_worked: e.target.value }))} data-testid="pay-days" /></div>
            </div>
            {/* Percepciones */}
            <div className="border-t border-slate-200 pt-3">
              <p className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1"><TrendingUp className="w-4 h-4" />Percepciones Adicionales</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Horas Extra</Label><Input type="number" value={newPayroll.overtime_hours} onChange={e => setNewPayroll(p => ({ ...p, overtime_hours: e.target.value }))} data-testid="pay-overtime" /></div>
                <div><Label className="text-xs">Domingos Trabajados</Label><Input type="number" value={newPayroll.sunday_worked} onChange={e => setNewPayroll(p => ({ ...p, sunday_worked: e.target.value }))} data-testid="pay-sunday" /></div>
                <div><Label className="text-xs">Bonos ($)</Label><Input type="number" value={newPayroll.bonuses} onChange={e => setNewPayroll(p => ({ ...p, bonuses: e.target.value }))} data-testid="pay-bonuses" /></div>
                <div><Label className="text-xs">Prima Vacacional ($)</Label><Input type="number" value={newPayroll.vacation_premium} onChange={e => setNewPayroll(p => ({ ...p, vacation_premium: e.target.value }))} data-testid="pay-vac-prem" /></div>
                <div><Label className="text-xs">Otras Percepciones ($)</Label><Input type="number" value={newPayroll.other_perceptions} onChange={e => setNewPayroll(p => ({ ...p, other_perceptions: e.target.value }))} data-testid="pay-other-perc" /></div>
              </div>
            </div>
            {/* Deducciones */}
            <div className="border-t border-slate-200 pt-3">
              <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1"><Shield className="w-4 h-4" />Deducciones Adicionales (ISR e IMSS se calculan automáticamente)</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">INFONAVIT ($)</Label><Input type="number" value={newPayroll.infonavit} onChange={e => setNewPayroll(p => ({ ...p, infonavit: e.target.value }))} data-testid="pay-infonavit" /></div>
                <div><Label className="text-xs">Pensión Alim. ($)</Label><Input type="number" value={newPayroll.pension_alimenticia} onChange={e => setNewPayroll(p => ({ ...p, pension_alimenticia: e.target.value }))} data-testid="pay-pension" /></div>
                <div><Label className="text-xs">Otras Deducciones ($)</Label><Input type="number" value={newPayroll.other_deductions} onChange={e => setNewPayroll(p => ({ ...p, other_deductions: e.target.value }))} data-testid="pay-other-ded" /></div>
              </div>
            </div>
            {/* Incidencias */}
            <div className="border-t border-slate-200 pt-3">
              <p className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Incidencias</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Faltas (días)</Label><Input type="number" value={newPayroll.absences} onChange={e => setNewPayroll(p => ({ ...p, absences: e.target.value }))} data-testid="pay-absences" /></div>
                <div><Label className="text-xs">Incapacidades (días)</Label><Input type="number" value={newPayroll.disabilities} onChange={e => setNewPayroll(p => ({ ...p, disabilities: e.target.value }))} data-testid="pay-disabilities" /></div>
              </div>
            </div>
            <Button onClick={handleCreate} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-payroll-btn">
              <Calculator className="w-4 h-4 mr-2" />Calcular y Guardar Nómina
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === BATCH DIALOG === */}
      <Dialog open={showBatchDlg} onOpenChange={setShowBatchDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Procesar Nómina Masiva</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 mb-4">Calcula automáticamente la nómina de todos los empleados activos con sus salarios registrados. ISR, IMSS y cuotas patronales se calculan con tablas mexicanas vigentes.</p>
          <div className="space-y-4">
            <div><Label>Período</Label><Input type="month" value={batchPeriod} onChange={e => setBatchPeriod(e.target.value)} data-testid="batch-period" /></div>
            <div><Label>Tipo de Nómina</Label>
              <Select value={batchType} onValueChange={setBatchType}>
                <SelectTrigger data-testid="batch-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="quincenal">Quincenal (15 días)</SelectItem><SelectItem value="mensual">Mensual (30 días)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
              Se procesarán <strong>{stats?.employees_count || 0}</strong> empleados activos para el período <strong>{batchPeriod}</strong>
            </div>
            <Button onClick={handleBatch} disabled={batchLoading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="run-batch-btn">
              {batchLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              {batchLoading ? "Procesando..." : "Procesar Nómina"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === DETAIL DIALOG === */}
      <Dialog open={showDetailDlg} onOpenChange={setShowDetailDlg}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedRecord && (() => {
            const r = selectedRecord;
            const perc = r.percepciones || {};
            const ded = r.deducciones || {};
            const pat = r.patronal || {};
            return (
              <>
                <DialogHeader><DialogTitle>Detalle de Nómina</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold">
                      {r.employee_name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "??"}
                    </div>
                    <div><p className="font-bold text-slate-900">{r.employee_name}</p><p className="text-xs text-slate-500">{r.department} | {r.period} ({r.period_type})</p></div>
                    <Badge className={STATUS_MAP[r.status]?.cls + " ml-auto"}>{STATUS_MAP[r.status]?.label}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 bg-slate-50 rounded-lg text-center"><p className="text-slate-400">SBC Mensual</p><p className="font-bold">{fmt(r.sbc_mensual)}</p></div>
                    <div className="p-2 bg-slate-50 rounded-lg text-center"><p className="text-slate-400">Salario Diario</p><p className="font-bold">{fmt(r.salario_diario)}</p></div>
                    <div className="p-2 bg-slate-50 rounded-lg text-center"><p className="text-slate-400">Días Trab.</p><p className="font-bold">{r.days_worked}/{r.period_days}</p></div>
                  </div>
                  <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50/50">
                    <p className="text-xs font-bold text-emerald-700 mb-2">PERCEPCIONES</p>
                    {[["Sueldo Período", perc.sueldo], ["Horas Extra", perc.horas_extra], ["Prima Dominical", perc.prima_dominical],
                      ["Bonos", perc.bonos], ["Prima Vacacional", perc.prima_vacacional], ["Otras", perc.otras]
                    ].filter(([, v]) => v > 0).map(([label, val]) => (
                      <div key={label} className="flex justify-between text-xs py-0.5"><span className="text-slate-600">{label}</span><span className="font-medium">{fmt(val)}</span></div>
                    ))}
                    <div className="flex justify-between text-sm font-bold border-t border-emerald-200 pt-1 mt-1"><span>Total</span><span className="text-emerald-700">{fmt(perc.total)}</span></div>
                  </div>
                  <div className="p-3 rounded-xl border border-red-200 bg-red-50/50">
                    <p className="text-xs font-bold text-red-700 mb-2">DEDUCCIONES</p>
                    {[["ISR (Art 96 LISR)", ded.isr], ["IMSS Obrero", ded.imss_obrero], ["INFONAVIT", ded.infonavit],
                      ["Pensión Alimenticia", ded.pension_alimenticia], ["Desc. Faltas", ded.desc_faltas],
                      ["Desc. Incapacidad", ded.desc_incapacidad], ["Otras", ded.otras]
                    ].filter(([, v]) => v > 0).map(([label, val]) => (
                      <div key={label} className="flex justify-between text-xs py-0.5"><span className="text-slate-600">{label}</span><span className="font-medium">{fmt(val)}</span></div>
                    ))}
                    <div className="flex justify-between text-sm font-bold border-t border-red-200 pt-1 mt-1"><span>Total</span><span className="text-red-700">{fmt(ded.total)}</span></div>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-100 border border-blue-300 text-center">
                    <p className="text-xs text-blue-600 font-medium">NETO A PAGAR</p>
                    <p className="text-2xl font-bold text-blue-800">{fmt(r.neto_a_pagar)}</p>
                  </div>
                  {pat.total_patronal > 0 && (
                    <div className="p-3 rounded-xl border border-violet-200 bg-violet-50/50">
                      <p className="text-xs font-bold text-violet-700 mb-2">CUOTAS PATRONALES</p>
                      {[["Riesgo de Trabajo", pat.riesgo_trabajo], ["Cesantía y Vejez", pat.cesantia_vejez_p],
                        ["Invalidez y Vida", pat.invalidez_vida_p], ["Guarderías", pat.guarderias],
                        ["Retiro (SAR)", pat.retiro], ["Enfermedad Especie", pat.enfermedad_especie_p],
                        ["INFONAVIT Patronal", pat.infonavit_patronal]
                      ].filter(([, v]) => v > 0).map(([label, val]) => (
                        <div key={label} className="flex justify-between text-xs py-0.5"><span className="text-slate-600">{label}</span><span className="font-medium">{fmt(val)}</span></div>
                      ))}
                      <div className="flex justify-between text-sm font-bold border-t border-violet-200 pt-1 mt-1"><span>Total Patronal</span><span className="text-violet-700">{fmt(pat.total_patronal)}</span></div>
                    </div>
                  )}
                  <div className="p-3 rounded-xl bg-slate-900 text-center">
                    <p className="text-xs text-slate-400">COSTO TOTAL EMPRESA</p>
                    <p className="text-xl font-bold text-white">{fmt(r.costo_empresa)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => handleDownloadReceipt(r.payroll_id)} data-testid="detail-download">
                      <Download className="w-4 h-4 mr-2" />Recibo
                    </Button>
                    {r.status === "pending" && (
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => { handleApprove(r.payroll_id); setShowDetailDlg(false); }}>
                        <Check className="w-4 h-4 mr-2" />Aprobar
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* === FINIQUITO SIMULATOR DIALOG === */}
      <Dialog open={showFiniquitoDlg} onOpenChange={(v) => { setShowFiniquitoDlg(v); if (!v) { setFiniquitoResult(null); setFiniquitoAI(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Scale className="w-5 h-5 text-indigo-600" />Simulador de Finiquito / Liquidación</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-xs text-indigo-700">
              Calcula automáticamente conforme a la Ley Federal del Trabajo (LFT): vacaciones proporcionales, aguinaldo, prima de antigüedad e indemnizaciones según el motivo de separación.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Empleado *</Label>
                <Select value={finiquitoForm.employee_id} onValueChange={v => setFiniquitoForm(p => ({ ...p, employee_id: v }))}>
                  <SelectTrigger data-testid="finiquito-employee"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Motivo de Separación *</Label>
                <Select value={finiquitoForm.reason} onValueChange={v => setFiniquitoForm(p => ({ ...p, reason: v }))}>
                  <SelectTrigger data-testid="finiquito-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="renuncia_voluntaria">Renuncia Voluntaria</SelectItem>
                    <SelectItem value="despido_injustificado">Despido Injustificado</SelectItem>
                    <SelectItem value="despido_justificado">Despido Justificado</SelectItem>
                    <SelectItem value="mutuo_acuerdo">Mutuo Acuerdo</SelectItem>
                    <SelectItem value="fin_contrato">Fin de Contrato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSimulateFiniquito} disabled={finiquitoLoading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="simulate-finiquito-btn">
              {finiquitoLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
              {finiquitoLoading ? "Calculando..." : "Simular Finiquito"}
            </Button>

            {finiquitoResult && (
              <div className="space-y-3 pt-2 border-t border-slate-200" data-testid="finiquito-result">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-bold text-xs">
                    {finiquitoResult.employee_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 text-sm">{finiquitoResult.employee_name}</p>
                    <p className="text-[10px] text-slate-500">{finiquitoResult.department} | Antigüedad: {finiquitoResult.years_worked} años, {finiquitoResult.months_worked} meses</p>
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-700">{finiquitoResult.reason_label}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded-lg bg-slate-50 text-center"><p className="text-slate-400">Salario Diario</p><p className="font-bold">{fmt(finiquitoResult.salary_daily)}</p></div>
                  <div className="p-2 rounded-lg bg-slate-50 text-center"><p className="text-slate-400">Días Vacaciones</p><p className="font-bold">{finiquitoResult.vacation_days}</p></div>
                </div>

                <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50/50">
                  <p className="text-xs font-bold text-emerald-700 mb-2">CONCEPTOS DE PAGO</p>
                  {[
                    ["Vacaciones Proporcionales", finiquitoResult.vacation_amount],
                    ["Prima Vacacional (25%)", finiquitoResult.prima_vacacional],
                    ["Aguinaldo Proporcional", finiquitoResult.aguinaldo],
                    ["Prima de Antigüedad", finiquitoResult.prima_antiguedad],
                  ].filter(([, v]) => v > 0).map(([label, val]) => (
                    <div key={label} className="flex justify-between text-xs py-0.5"><span className="text-slate-600">{label}</span><span className="font-medium">{fmt(val)}</span></div>
                  ))}
                  <div className="flex justify-between text-sm font-bold border-t border-emerald-200 pt-1 mt-1"><span>Subtotal Finiquito</span><span className="text-emerald-700">{fmt(finiquitoResult.subtotal_finiquito)}</span></div>
                </div>

                {(finiquitoResult.indemnizacion_3m > 0 || finiquitoResult.indemnizacion_20d > 0) && (
                  <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/50">
                    <p className="text-xs font-bold text-amber-700 mb-2">INDEMNIZACIONES (LFT)</p>
                    {finiquitoResult.indemnizacion_3m > 0 && (
                      <div className="flex justify-between text-xs py-0.5"><span className="text-slate-600">3 Meses de Salario (Art. 48)</span><span className="font-medium">{fmt(finiquitoResult.indemnizacion_3m)}</span></div>
                    )}
                    {finiquitoResult.indemnizacion_20d > 0 && (
                      <div className="flex justify-between text-xs py-0.5"><span className="text-slate-600">20 Días por Año (Art. 50)</span><span className="font-medium">{fmt(finiquitoResult.indemnizacion_20d)}</span></div>
                    )}
                  </div>
                )}

                <div className="p-3 rounded-xl border border-red-200 bg-red-50/50">
                  <div className="flex justify-between text-xs"><span className="text-slate-600">ISR Retención Estimada</span><span className="font-medium text-red-600">- {fmt(finiquitoResult.isr_retencion)}</span></div>
                </div>

                <div className="p-4 rounded-xl bg-indigo-100 border border-indigo-300 text-center">
                  <p className="text-xs text-indigo-600 font-medium">TOTAL NETO A PAGAR</p>
                  <p className="text-2xl font-bold text-indigo-800">{fmt(finiquitoResult.neto_a_pagar)}</p>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={handleFiniquitoAI} disabled={aiLoading} data-testid="finiquito-ai-btn">
                    {aiLoading ? <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2 text-violet-600" />}
                    {aiLoading ? "Analizando..." : "Análisis Legal IA"}
                  </Button>
                </div>

                {finiquitoAI && (
                  <div className="space-y-2 pt-2 border-t border-slate-200" data-testid="finiquito-ai-result">
                    <p className="text-xs font-bold text-violet-700 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" />Análisis IA - Derecho Laboral Mexicano</p>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={finiquitoAI.risk_level === "alto" ? "bg-red-100 text-red-700" : finiquitoAI.risk_level === "medio" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                        Riesgo: {finiquitoAI.risk_level}
                      </Badge>
                    </div>
                    {finiquitoAI.legal_notes?.length > 0 && (
                      <div className="p-2.5 rounded-lg bg-blue-50"><p className="text-[10px] font-bold text-blue-700 mb-1">Notas Legales</p>
                        {finiquitoAI.legal_notes.map((n, i) => <p key={i} className="text-xs text-slate-700 flex items-start gap-1"><ArrowRight className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />{n}</p>)}
                      </div>
                    )}
                    {finiquitoAI.recommendations?.length > 0 && (
                      <div className="p-2.5 rounded-lg bg-emerald-50"><p className="text-[10px] font-bold text-emerald-700 mb-1">Recomendaciones</p>
                        {finiquitoAI.recommendations.map((r, i) => <p key={i} className="text-xs text-slate-700 flex items-start gap-1"><Lightbulb className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />{r}</p>)}
                      </div>
                    )}
                    {finiquitoAI.warnings?.length > 0 && (
                      <div className="p-2.5 rounded-lg bg-red-50"><p className="text-[10px] font-bold text-red-700 mb-1">Advertencias</p>
                        {finiquitoAI.warnings.map((w, i) => <p key={i} className="text-xs text-slate-700 flex items-start gap-1"><AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />{w}</p>)}
                      </div>
                    )}
                    {finiquitoAI.savings_tips?.length > 0 && (
                      <div className="p-2.5 rounded-lg bg-amber-50"><p className="text-[10px] font-bold text-amber-700 mb-1">Optimización de Costos</p>
                        {finiquitoAI.savings_tips.map((t, i) => <p key={i} className="text-xs text-slate-700 flex items-start gap-1"><DollarSign className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />{t}</p>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showProviderDlg} onOpenChange={setShowProviderDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Configuración STP y credenciales</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Entorno</Label><Select value={providerConfig.environment || "sandbox"} onValueChange={value => setProviderConfig(previous => ({ ...previous, environment: value }))}><SelectTrigger data-testid="provider-environment-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="sandbox">Sandbox</SelectItem><SelectItem value="production">Producción</SelectItem></SelectContent></Select></div>
              <div><Label>Empresa STP</Label><Input value={providerConfig.stp_company_code || ""} onChange={event => setProviderConfig(previous => ({ ...previous, stp_company_code: event.target.value }))} data-testid="provider-company-code-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>CLABE ordenante</Label><Input value={providerConfig.origin_clabe || ""} onChange={event => setProviderConfig(previous => ({ ...previous, origin_clabe: event.target.value }))} data-testid="provider-origin-clabe-input" /></div>
              <div><Label>Banco contraparte</Label><Input value={providerConfig.counterparty_bank_code || "90646"} onChange={event => setProviderConfig(previous => ({ ...previous, counterparty_bank_code: event.target.value }))} data-testid="provider-counterparty-bank-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Usuario API</Label><Input value={providerConfig.credentials?.username || ""} onChange={event => setProviderConfig(previous => ({ ...previous, credentials: { ...previous.credentials, username: event.target.value } }))} data-testid="provider-api-username-input" /></div>
              <div><Label>Password API</Label><Input type="password" value={providerConfig.credentials?.password || ""} onChange={event => setProviderConfig(previous => ({ ...previous, credentials: { ...previous.credentials, password: event.target.value } }))} data-testid="provider-api-password-input" /></div>
              <div><Label>Passphrase llave</Label><Input type="password" value={providerConfig.credentials?.private_key_passphrase || ""} onChange={event => setProviderConfig(previous => ({ ...previous, credentials: { ...previous.credentials, private_key_passphrase: event.target.value } }))} data-testid="provider-api-passphrase-input" /></div>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
              Endpoints STP preparados: <strong>{providerConfig.base_url || "https://sandbox.stpmx.com"}</strong> · Orden <strong>{providerConfig.create_order_endpoint || "/spei/v1/orders"}</strong>
            </div>
          </div>
          <DialogFooter><Button onClick={handleSaveProviderConfig} className="bg-sky-600 hover:bg-sky-700" data-testid="save-provider-config-btn">Guardar configuración</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDisperserDlg} onOpenChange={setShowDisperserDlg}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dispersador enterprise</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={loadDisperserPreview} disabled={disperserLoading} data-testid="load-disperser-preview-btn">Vista previa</Button>
              <Button onClick={handleCreateDisperserBatch} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-disperser-batch-btn">Crear lote</Button>
            </div>
            {disperserPreview && (
              <div className="space-y-4" data-testid="disperser-preview-card">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-400 uppercase">Pago interno</p><p className="text-2xl font-bold text-slate-900">{fmt(disperserPreview.internal_total)}</p><p className="text-xs text-slate-500">{disperserPreview.internal_items?.length || 0} dispersiones</p></CardContent></Card>
                  <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-400 uppercase">Partidas externas</p><p className="text-2xl font-bold text-slate-900">{fmt(disperserPreview.external_total)}</p><p className="text-xs text-slate-500">{disperserPreview.external_items?.length || 0} referencias</p></CardContent></Card>
                </div>
                {disperserPreview.warnings?.length > 0 && <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">{disperserPreview.warnings.map((warning, index) => <p key={index} className="text-xs text-amber-700">• {warning}</p>)}</div>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Payroll;
